import { createAsyncThunk, createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import differenceInSeconds from "date-fns/differenceInSeconds";

import {
  ITagData,
  TagValuesStr,
  IStringToTimeValueDictionary,
  IMultiSiteStringToTimeValueDictionary,
  TagTimeValidity,
  IValueTime,
  PRODUCTION_TO_POTENTIAL_FACTOR,
} from "../../data_point/models/TagsModels";
import APP_CONFIG from "../configuration/AppConfig";
import { RootState } from "../Store";
import { resetSiteSpecificData } from "./GlobalActions";
import { Tags } from "../../data_point/tags/TagCatalog";
import { LoadingState } from "../LoadingState";
import { getTagsLatestAsync } from "../TagsAPI";
import { createAllDevicesTagSelection, createScalarAndSpecificTagSelections } from "../TagQueryUtils";
import { isTagForDevice } from "../../data_point/tags/TagCatalogUtils";
import { beginRequest, completeRequest, rejectRequest, RequestState } from "./RequestState";

const invalidValueTime: ITagData = {
  value: -1,
  time: new Date(0),
  timeValidity: TagTimeValidity.Invalid,
  valueExist: false,
};

type StoredTag = Omit<ITagData, "time"> & { time: number };
type StoredLatestValues = Record<string, { value: number; time: number }>;
export const serializeLatestValues = (values: IStringToTimeValueDictionary): StoredLatestValues =>
  Object.fromEntries(
    Object.entries(values).map(([tag, sample]) => [tag, { ...sample, time: sample.time.valueOf() }])
  );
interface IMultisiteTagsState {
  tagsCache: Record<string, Record<string, StoredTag>>;
  latestRequestStatusBySite: Record<string, LoadingState>;
  firmwareStatusRequestsBySite: Record<string, RequestState>;
  firmwareStatusSnapshotLoadedBySite: Record<string, boolean>;
}

const initialState: IMultisiteTagsState = {
  tagsCache: {},
  latestRequestStatusBySite: {},
  firmwareStatusRequestsBySite: {},
  firmwareStatusSnapshotLoadedBySite: {},
};
export const fetchFirmwareStatusTags = createAsyncThunk(
  "multisiteTags/firmwareStatus/fetch",
  (siteId: string, { signal }) =>
    getTagsLatestAsync(
      siteId,
      [
        createAllDevicesTagSelection(Tags.TrackerStatus.TrackerAutoFirmwareUpdateStatus.logicalName),
        ...createScalarAndSpecificTagSelections([
          Tags.McsState.TcmFirmwareAutoUpdateMode.master,
          Tags.McsState.TimeToNextAutoFWUpdate.master,
        ]),
      ],
      signal
    ).then(serializeLatestValues),
  {
    condition: (siteId, { getState }) =>
      Boolean(siteId) &&
      (getState() as RootState).multiSitesTags.firmwareStatusRequestsBySite[siteId]?.loadingState !==
        LoadingState.Pending,
  }
);

function overridePotentialTagIfNeeded(siteTags: Record<string, StoredTag>) {
  const acPowerTag = siteTags[Tags.Portal.Inverter.AcPower.aggregated];
  const potentialTag = siteTags[Tags.Portal.PerformanceModel.PotentialPowerProduction.logicalName];
  const potentialExist = potentialTag?.valueExist ?? false;
  const acPowerExist = acPowerTag?.valueExist ?? false;
  if (
    potentialExist &&
    acPowerExist &&
    acPowerTag.timeValidity === TagTimeValidity.Valid &&
    acPowerTag.value > potentialTag.value
  ) {
    siteTags[Tags.Portal.PerformanceModel.PotentialPowerProduction.logicalName] = {
      ...potentialTag,
      value: acPowerTag.value * PRODUCTION_TO_POTENTIAL_FACTOR,
    };
  }
}

function calculatePerformanceIndexTagIfNeeded(siteTags: Record<string, StoredTag>) {
  // We decided not to use the original virtual tag of perfIndex. Instead, we calculate it by ourself
  // to make sure that the performance index fits the actual ratio between current potential and acPower in the store.
  const acPowerTag = siteTags[Tags.Portal.Inverter.AcPower.aggregated];
  const potentialTag = siteTags[Tags.Portal.PerformanceModel.PotentialPowerProduction.logicalName];
  const potentialExist = potentialTag?.valueExist ?? false;
  const acPowerExist = acPowerTag?.valueExist ?? false;

  const potentialExistsButInvalid =
    potentialExist && (potentialTag.timeValidity !== TagTimeValidity.Valid || potentialTag.value === 0);
  const acPowerExistsButInvalid = acPowerExist && acPowerTag.timeValidity !== TagTimeValidity.Valid;

  if (potentialExistsButInvalid || acPowerExistsButInvalid) {
    siteTags[Tags.FrontendCalculated.CurrentPerformanceIndex.logicalName] = {
      value: 0,
      time: 0,
      timeValidity: TagTimeValidity.Invalid,
      valueExist: true,
    };
    return;
  }

  if (potentialExist && acPowerExist) {
    siteTags[Tags.FrontendCalculated.CurrentPerformanceIndex.logicalName] = {
      time: potentialTag.time,
      timeValidity: potentialTag.timeValidity,
      value: (acPowerTag.value / potentialTag.value) * 100,
      valueExist: true,
    };
  }
}

const updateSiteData = (state: IMultisiteTagsState, siteId: string, data: StoredLatestValues) => {
  const now = Date.now();

  if (!state.tagsCache[siteId]) {
    state.tagsCache[siteId] = {};
  }
  Object.entries(data).forEach(([key, valuePair]) => {
    const time = valuePair.time;
    state.tagsCache[siteId][key] = {
      value: valuePair.value,
      time: time,
      timeValidity: calculateValidity(now, time),
      valueExist: true,
    };
  });
  const siteTags = state.tagsCache[siteId];
  overridePotentialTagIfNeeded(siteTags);
  calculatePerformanceIndexTagIfNeeded(siteTags);
};

const calculateValidity = (now: number, tagTime: number) => {
  const secondsDelta = differenceInSeconds(now, tagTime);
  if (secondsDelta < APP_CONFIG.tagStatusOldSecondsThreshold) {
    return TagTimeValidity.Valid;
  }
  if (secondsDelta < APP_CONFIG.tagStatusInvalidSecondsThreshold) {
    return TagTimeValidity.ValidButOld;
  }
  return TagTimeValidity.Invalid;
};

export const multisiteTagsSlice = createSlice({
  name: "multisiteTags",
  initialState,
  reducers: {
    updateSiteTags: {
      prepare: (args: { siteId: string; data: IStringToTimeValueDictionary }) => ({
        payload: { siteId: args.siteId, data: serializeLatestValues(args.data) },
      }),
      reducer: (
        state: IMultisiteTagsState,
        action: PayloadAction<{
          siteId: string;
          data: StoredLatestValues;
        }>
      ) => {
        const { siteId, data } = action.payload;
        updateSiteData(state, siteId, data);
        state.latestRequestStatusBySite[siteId] = LoadingState.Complete;
      },
    },
    updateMultiSiteTags: {
      prepare: (data: IMultiSiteStringToTimeValueDictionary) => ({
        payload: Object.fromEntries(
          Object.entries(data).map(([siteId, values]) => [siteId, serializeLatestValues(values)])
        ),
      }),
      reducer: (
        state: IMultisiteTagsState,
        action: PayloadAction<Record<string, StoredLatestValues>>
      ) => {
        Object.entries(action.payload).forEach(([siteId, data]) => {
          updateSiteData(state, siteId, data);
          state.latestRequestStatusBySite[siteId] = LoadingState.Complete;
        });
      },
    },
    latestRequestStarted: (state, action: PayloadAction<string>) => {
      state.latestRequestStatusBySite[action.payload] = LoadingState.Pending;
    },
    latestRequestFailed: (state, action: PayloadAction<string>) => {
      state.latestRequestStatusBySite[action.payload] = LoadingState.Error;
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase(fetchFirmwareStatusTags.pending, (state, action) => {
        beginRequest(
          (state.firmwareStatusRequestsBySite[action.meta.arg] ??= { loadingState: LoadingState.None }),
          action.meta.requestId
        );
      })
      .addCase(fetchFirmwareStatusTags.fulfilled, (state, action) => {
        const entry = state.firmwareStatusRequestsBySite[action.meta.arg];
        if (!entry || !completeRequest(entry, action.meta.requestId)) {
          return;
        }
        const tags = state.tagsCache[action.meta.arg];
        // The response replaces every requested firmware value, including absent master values.
        for (const tag of Object.keys(tags ?? {})) {
          if (
            isTagForDevice(Tags.TrackerStatus.TrackerAutoFirmwareUpdateStatus.logicalName, tag) ||
            tag === Tags.McsState.TcmFirmwareAutoUpdateMode.master ||
            tag === Tags.McsState.TimeToNextAutoFWUpdate.master
          ) {
            delete tags[tag];
          }
        }
        updateSiteData(state, action.meta.arg, action.payload);
        state.firmwareStatusSnapshotLoadedBySite[action.meta.arg] = true;
      })
      .addCase(fetchFirmwareStatusTags.rejected, (state, action) => {
        const entry = state.firmwareStatusRequestsBySite[action.meta.arg];
        if (entry) {
          rejectRequest(entry, action);
        }
      })
      .addCase(resetSiteSpecificData, () => {
        return initialState;
      }),
});

const tagViews = new WeakMap<StoredTag, ITagData>();
function tagView(tag: StoredTag | undefined): ITagData {
  if (!tag) {
    return invalidValueTime;
  }
  let view = tagViews.get(tag);
  if (!view) {
    view = { ...tag, time: new Date(tag.time) };
    tagViews.set(tag, view);
  }
  return view;
}
export const selectSiteTag = (state: RootState, siteId: string, tagName: string): ITagData =>
  tagView(state.multiSitesTags.tagsCache[siteId]?.[tagName]);

export const selectIsTagValid = (state: RootState, siteId: string, tagName: string): boolean =>
  state.multiSitesTags.tagsCache[siteId]?.[tagName]?.timeValidity === TagTimeValidity.Valid;

export const selectLatestTagsLoadingState = (state: RootState, siteId: string): LoadingState =>
  state.multiSitesTags.latestRequestStatusBySite?.[siteId] ?? LoadingState.None;

export const selectSiteTagDataStr = createSelector(
  [
    (state: RootState, siteId: string, tagName: string) =>
      state.multiSitesTags.tagsCache[siteId]?.[tagName] ?? invalidValueTime,
  ],
  (valueTime): TagValuesStr => ({
    value: parseFloat(valueTime.value.toFixed(1)).toString(),
    timestamp: new Date(valueTime.time),
  })
);

export const selectFieldState = createSelector(
  [
    (state: RootState, siteId: string) =>
      selectSiteTag(state, siteId, Tags.Portal.System.FieldState.logicalName),
  ],
  (tagData): IValueTime => {
    return { value: tagData.value, time: tagData.time };
  }
);

export const selectSafetyReason = createSelector(
  [
    (state: RootState, siteId: string) =>
      selectSiteTag(state, siteId, Tags.Portal.System.SafetyReason.logicalName),
  ],
  (tagData) => tagData.value
);

export const multiSiteTagsActions = multisiteTagsSlice.actions;
export const multisiteTagsReducer = multisiteTagsSlice.reducer;
